import { PRIMARY_USE_OPTIONS } from '@parcel/filters';
import type { PROJECT_ACCOUNT_ROLE_VALUES } from '@parcel/types';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { SortingState } from '@tanstack/react-table';
import { useEffect, useMemo } from 'react';
import { apiFetch } from './api.js';
import { BROWSE_PAGE_SIZE, firstServerSort } from './browse-query.js';
import type { FieldEvidenceMap } from './field-evidence.js';
import { type FilterParamEntries, appendFilterEntries, filterEntriesKey } from './filter-params.js';
import { type ProjectRow, UNKNOWN_PROJECT_TYPE } from './projects-display.js';
import type { RecordScores } from './scores.js';

const PAGE_SIZE = 1000;
// Hard cap on auto-paginated rows in the UI. Beyond this, the user should
// filter server-side via /projects?stage=… etc. or we'll need to introduce
// table-level server pagination. A full load is now at most two pages.
const AUTO_PAGE_CAP = 2000;
// Keep an already-loaded list "fresh" for 5 min so returning to the page serves
// cached rows instantly instead of re-streaming every page.
const LIST_STALE_TIME_MS = 5 * 60_000;

// Wire types mirror the API's public OpenAPI schema (snake_case). Customers
// consuming /api/v1/projects directly see the same shapes — keep these in sync
// with apps/api/src/routes/projects.ts schemas.

export interface ApiProjectSummary {
  id: string;
  name: string | null;
  primary_address: string;
  city: string;
  state: string;
  neighborhood: string | null;
  primary_use:
    | 'multifamily'
    | 'mixed_use'
    | 'office'
    | 'industrial'
    | 'retail'
    | 'hotel'
    | 'institutional'
    | 'educational'
    | 'r_and_d'
    | 'other'
    | null;
  uses: string[];
  stage:
    | 'pre_filing'
    | 'filed'
    | 'under_review'
    | 'approved'
    | 'permitted'
    | 'under_construction'
    | 'completed'
    | null;
  disposition: 'active' | 'denied' | 'withdrawn' | 'expired' | 'stalled' | 'cancelled';
  /** This workspace's Atlas Score; an older API omits it. */
  scores?: RecordScores;
  last_signal_at: string | null;
  first_signal_at: string | null;
  gross_floor_area_sf: number | null;
  residential_units: number | null;
  cost_of_construction_usd: number | null;
  /** Null means no source states a storey count, not a one-storey building. */
  stories: number | null;
  /** PR11.5; absent from an API that predates it (the web deploys before the API). */
  parking_spaces?: number | null;
  gc_status?: ApiGcStatus;
  lat: string | null;
  lng: string | null;
  latest_source: ApiProjectSource | null;
}

/** named: a contractor is linked; not_yet_selected: a filing says none is chosen; unknown: no source says. */
export type ApiGcStatus = 'named' | 'not_yet_selected' | 'unknown';

export interface ApiProjectSource {
  id: string;
  slug: string;
  display_name: string;
  base_url: string;
  logo_domain: string | null;
}

export interface ApiProject extends ApiProjectSummary {
  description: string | null;
  project_website_url: string | null;
  external_ids: Record<string, unknown>;
  source_phases: Record<string, unknown>;
  regulatory_flags: string[];
  additional_addresses: string[];
  zip: string | null;
  land_area_sf: number | null;
  /** The facts below are PR11.5's; absent from an API that predates it (the web deploys first). */
  height_ft?: number | null;
  hotel_rooms?: number | null;
  unit_mix?: {
    studio_or_1br: number | null;
    two_br: number | null;
    three_br_plus: number | null;
  } | null;
  affordable_units?: {
    ami_0_30: number | null;
    ami_30_50: number | null;
    ami_50_80: number | null;
    ami_80_120: number | null;
    ami_unknown: number | null;
  } | null;
  use_areas?: Array<{ use_type: string; floor_area_sf: number }>;
  structure_types?: string[];
  /** Derived from floor area over land area; no source states it. */
  far?: { value: number; derived_from: 'gross_floor_area_sf/land_area_sf' } | null;
  updates_last_30_days?: number;
  field_evidence?: FieldEvidenceMap;
  media?: ApiProjectMedia;
}

export interface ApiProjectImage {
  id: string;
  kind: 'rendering' | 'site_plan' | 'photo' | 'unknown';
  /** A Parcel API path; fetch it with the session, never a publisher URL. */
  url: string;
  width: number | null;
  height: number | null;
  attribution: {
    publisher_name: string;
    publisher_domain: string;
    source_page_url: string;
    /** The filing an image came from; null for a web image. */
    document_type: string | null;
    document_date: string | null;
    page_number: number | null;
  };
}

export interface ApiProjectMedia {
  primary_image: ApiProjectImage | null;
  images: ApiProjectImage[];
  source_links: Array<{ page_url: string; publisher_name: string; publisher_domain: string }>;
  site_location: { lat: number; lng: number } | null;
}

export interface ApiPagination {
  limit: number;
  offset: number;
  total: number | null;
  next_cursor?: string | null;
  has_more?: boolean;
  as_of?: string;
}

export interface ApiProjectListResponse {
  projects: ApiProjectSummary[];
  pagination: ApiPagination;
}

export interface ApiProjectDetailResponse {
  project: ApiProject;
}

export type ApiProjectAccountType =
  | 'developer'
  | 'architect'
  | 'gc'
  | 'engineer'
  | 'agency'
  | 'law_firm'
  | 'consultant'
  | 'landscape_architect'
  | 'other';

/** The role an account plays on a project; owner is a role, never an account type. */
export type ApiProjectAccountRole = (typeof PROJECT_ACCOUNT_ROLE_VALUES)[number];

export interface ApiProjectAccountSubResource {
  id: string;
  name: string;
  account_types: ApiProjectAccountType[];
  domains: string[];
  website: string | null;
  roles: ApiProjectAccountRole[];
  effective_roles: string[];
}

export interface ApiProjectAccountsResponse {
  accounts: ApiProjectAccountSubResource[];
  pagination: { limit: number; offset: number; total: number | null };
}

export interface ProjectListParams {
  workspaceId: string;
  limit?: number;
  offset?: number;
  filter?: FilterParamEntries;
}

// DB and web stage vocabularies are now identical (both canonical). No crosswalk needed.

// Single source: the Type column displays the registry's filter labels, so
// filtering and the column always speak the same language.
const PRIMARY_USE_LABEL = Object.fromEntries(
  PRIMARY_USE_OPTIONS.map((option) => [option.value, option.label]),
) as Record<NonNullable<ApiProjectSummary['primary_use']>, string>;

// Returns NaN for missing/unparseable coordinates. Callers must guard with
// Number.isFinite before rendering — see ProjectsMap (skips marker) and
// ProjectDetail (renders "—") for the two consumers.
function parseCoord(value: string | null): number {
  if (value == null) return Number.NaN;
  const n = Number(value);
  return Number.isFinite(n) ? n : Number.NaN;
}

function projectTypeLabel(p: ApiProjectSummary): string {
  if (p.primary_use) return PRIMARY_USE_LABEL[p.primary_use] ?? p.primary_use;
  const firstUse = p.uses[0];
  return firstUse ?? UNKNOWN_PROJECT_TYPE;
}

export function apiProjectToRow(p: ApiProjectSummary): ProjectRow {
  // null = no derived stage; pass it through (rendered as "Unknown") rather than
  // coercing to pre_filing, so the column matches the stage filter.
  const stage = p.stage;
  const detectedAt = p.last_signal_at ?? p.first_signal_at ?? new Date(0).toISOString();
  return {
    id: p.id,
    name: p.name ?? p.primary_address,
    address: p.primary_address,
    city: p.city,
    state: p.state,
    type: projectTypeLabel(p),
    // null = size not reported; pass it through (rendered as "—") rather than coercing to 0.
    sqft: p.gross_floor_area_sf,
    stage,
    disposition: p.disposition,
    developer: '—',
    architect: '—',
    detectedAt,
    source: p.latest_source?.display_name ?? '—',
    sourceBaseUrl: p.latest_source?.base_url ?? null,
    sourceLogoDomain: p.latest_source?.logo_domain ?? null,
    lat: parseCoord(p.lat),
    lng: parseCoord(p.lng),
    scores: p.scores ?? {},
  };
}

function buildListQuery(params: ProjectListParams & { viewId?: string }): string {
  const qs = new URLSearchParams({ workspace_id: params.workspaceId });
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  if (params.viewId) qs.set('view_id', params.viewId);
  appendFilterEntries(qs, params.filter);
  return qs.toString();
}

function useLegacyWorkspaceProjects(
  workspaceId: string | undefined,
  filter?: FilterParamEntries,
  staticViewId?: string,
  options?: { enabled?: boolean },
) {
  const enabled = Boolean(workspaceId) && (options?.enabled ?? true);
  const infinite = useInfiniteQuery({
    queryKey: ['projects', workspaceId, filterEntriesKey(filter), staticViewId ?? null],
    enabled,
    initialPageParam: 0,
    placeholderData: keepPreviousData,
    staleTime: LIST_STALE_TIME_MS,
    queryFn: async ({ pageParam }) => {
      const qs = buildListQuery({
        workspaceId: workspaceId ?? '',
        limit: PAGE_SIZE,
        offset: pageParam,
        filter,
        viewId: staticViewId,
      });
      return apiFetch<ApiProjectListResponse>(`/projects?${qs}`);
    },
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, p) => n + p.projects.length, 0);
      if (lastPage.projects.length < PAGE_SIZE) return undefined;
      if (loaded >= AUTO_PAGE_CAP) return undefined;
      return loaded;
    },
  });

  // Auto-advance through pages so the table/map see every project the
  // workspace can access (up to AUTO_PAGE_CAP). When we add server-side
  // table pagination this side-effect goes away.
  // Guard: skip auto-fetch while serving placeholder data from a previous key
  // so we don't trigger fetchNextPage on stale pages before the new query lands.
  useAutoFetchNextPage(
    enabled,
    infinite.hasNextPage,
    infinite.isFetchingNextPage,
    infinite.fetchNextPage,
    infinite.isPlaceholderData,
  );

  const projects = useMemo(
    () => (infinite.data?.pages ?? []).flatMap((page) => page.projects),
    [infinite.data?.pages],
  );
  // Real filtered total from page one (null when unknown or count failed);
  // loaded rows remain projects.length.
  const total = infinite.data?.pages[0]?.pagination.total ?? null;
  const data = useMemo(
    () => ({
      projects,
      pagination: { limit: PAGE_SIZE, offset: 0, total },
    }),
    [projects, total],
  );

  return useMemo(
    () => ({
      data,
      isLoading: infinite.isLoading,
      isPlaceholderData: infinite.isPlaceholderData,
      isError: infinite.isError,
      isFetching: infinite.isFetching || infinite.isFetchingNextPage,
      fetchNextPage: infinite.fetchNextPage,
      hasNextPage: infinite.hasNextPage,
      isFetchingNextPage: infinite.isFetchingNextPage,
      isFetchNextPageError: infinite.isFetchNextPageError,
    }),
    [
      data,
      infinite.isError,
      infinite.isFetching,
      infinite.isFetchingNextPage,
      infinite.isFetchNextPageError,
      infinite.fetchNextPage,
      infinite.hasNextPage,
      infinite.isLoading,
      infinite.isPlaceholderData,
    ],
  );
}

const PROJECT_BROWSE_SORTS = ['name', 'detectedAt', 'atlasScore'] as const;
const PROJECT_BROWSE_FALLBACK = { id: 'detectedAt', desc: true };

function useCursorWorkspaceProjects(
  workspaceId: string | undefined,
  filter: FilterParamEntries | undefined,
  staticViewId: string | undefined,
  sorting: SortingState,
  enabled: boolean,
) {
  const { sort, direction } = firstServerSort(
    sorting,
    PROJECT_BROWSE_FALLBACK,
    PROJECT_BROWSE_SORTS,
  );
  const infinite = useInfiniteQuery({
    queryKey: [
      'projects-browse',
      workspaceId,
      filterEntriesKey(filter),
      staticViewId ?? null,
      sort,
      direction,
    ],
    enabled: Boolean(workspaceId) && enabled,
    initialPageParam: null as string | null,
    staleTime: LIST_STALE_TIME_MS,
    queryFn: async ({ pageParam }) => {
      const qs = new URLSearchParams({
        workspace_id: workspaceId ?? '',
        limit: String(BROWSE_PAGE_SIZE),
        sort,
        direction,
      });
      if (pageParam) qs.set('cursor', pageParam);
      if (staticViewId) qs.set('view_id', staticViewId);
      appendFilterEntries(qs, filter);
      return apiFetch<ApiProjectListResponse>(`/projects?${qs.toString()}`);
    },
    getNextPageParam: (lastPage) => lastPage.pagination.next_cursor ?? undefined,
  });

  const projects = useMemo(
    () => (infinite.data?.pages ?? []).flatMap((page) => page.projects),
    [infinite.data?.pages],
  );
  const data = useMemo(
    () => ({
      projects,
      pagination: infinite.data?.pages[0]?.pagination ?? {
        limit: BROWSE_PAGE_SIZE,
        offset: 0,
        total: null,
      },
    }),
    [infinite.data?.pages, projects],
  );

  return useMemo(
    () => ({
      data,
      isLoading: infinite.isLoading,
      isPlaceholderData: false,
      isError: infinite.isError,
      isFetching: infinite.isFetching,
      fetchNextPage: infinite.fetchNextPage,
      hasNextPage: infinite.hasNextPage,
      isFetchingNextPage: infinite.isFetchingNextPage,
      isFetchNextPageError: infinite.isFetchNextPageError,
    }),
    [
      data,
      infinite.fetchNextPage,
      infinite.hasNextPage,
      infinite.isError,
      infinite.isFetchNextPageError,
      infinite.isFetching,
      infinite.isFetchingNextPage,
      infinite.isLoading,
    ],
  );
}

export interface WorkspaceProjectsOptions {
  enabled?: boolean;
  browseMode?: boolean;
  sorting?: SortingState;
}

export function useWorkspaceProjects(
  workspaceId: string | undefined,
  filter?: FilterParamEntries,
  staticViewId?: string,
  options?: WorkspaceProjectsOptions,
) {
  const enabled = options?.enabled ?? true;
  const browseMode = options?.browseMode ?? false;
  const legacy = useLegacyWorkspaceProjects(workspaceId, filter, staticViewId, {
    enabled: enabled && !browseMode,
  });
  const cursor = useCursorWorkspaceProjects(
    workspaceId,
    filter,
    staticViewId,
    options?.sorting ?? [],
    enabled && browseMode,
  );

  return browseMode ? cursor : legacy;
}

// Small, contained helper to keep the auto-paginate loop out of the public hook surface.
// isPlaceholderData guards against firing fetchNextPage on stale pages from a
// previous query key while the new filtered query is in flight.
function useAutoFetchNextPage(
  enabled: boolean,
  hasNextPage: boolean,
  isFetchingNextPage: boolean,
  fetchNextPage: () => Promise<unknown>,
  isPlaceholderData = false,
) {
  useEffect(() => {
    if (enabled && hasNextPage && !isFetchingNextPage && !isPlaceholderData) {
      void fetchNextPage();
    }
  }, [enabled, hasNextPage, isFetchingNextPage, fetchNextPage, isPlaceholderData]);
}

/**
 * Tiny unpaginated lookup powering record typeaheads (global search, filter
 * bar). Callers are expected to debounce `q` — every distinct trimmed value
 * past 2 chars is a new query key and fires a request.
 */
export function useProjectSearchPreview(
  workspaceId: string | undefined,
  q: string,
  scopeFilter?: FilterParamEntries,
  staticViewId?: string,
) {
  const trimmed = q.trim();
  const previewFilter = [
    ...(scopeFilter ?? []).filter(([key]) => key !== 'q'),
    ['q', trimmed] as const,
  ];
  return useQuery({
    queryKey: [
      'projects-preview',
      workspaceId,
      filterEntriesKey(previewFilter),
      staticViewId ?? null,
    ],
    enabled: Boolean(workspaceId) && trimmed.length >= 2,
    staleTime: 10_000,
    queryFn: async () => {
      const qs = new URLSearchParams({ workspace_id: workspaceId ?? '', limit: '5' });
      if (staticViewId) qs.set('view_id', staticViewId);
      appendFilterEntries(qs, scopeFilter);
      qs.set('q', trimmed);
      return apiFetch<ApiProjectListResponse>(`/projects?${qs.toString()}`);
    },
  });
}

/** The drawer's project request, shared so Spec's photos prefetch warms the drawer too. */
export function projectDetailQuery(projectId: string | null, workspaceId: string | undefined) {
  return {
    queryKey: ['project-detail', projectId, workspaceId] as const,
    queryFn: async () => {
      const qs = new URLSearchParams({ workspace_id: workspaceId ?? '' }).toString();
      return apiFetch<ApiProjectDetailResponse>(
        `/projects/${encodeURIComponent(projectId ?? '')}?${qs}`,
      );
    },
  };
}

export function useProjectDetail(projectId: string | null, workspaceId: string | undefined) {
  return useQuery({
    ...projectDetailQuery(projectId, workspaceId),
    enabled: Boolean(projectId) && Boolean(workspaceId),
  });
}

export interface ApiProjectDocument {
  id: string;
  document_type: string;
  /** YYYY-MM-DD; null when the document states no date. */
  document_date: string | null;
  source_url: string;
  source_label: string;
  publisher_domain: string | null;
  has_extracted_facts: boolean;
}

export interface ApiProjectDocumentsResponse {
  documents: ApiProjectDocument[];
  total: number;
  next_cursor: string | null;
}

/** A project's filings and cited web pages, newest first, paged by cursor. */
export function useProjectDocuments(projectId: string | null, workspaceId: string | undefined) {
  return useInfiniteQuery({
    queryKey: ['project-documents', projectId, workspaceId],
    enabled: Boolean(projectId) && Boolean(workspaceId),
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const qs = new URLSearchParams({ workspace_id: workspaceId ?? '', limit: '25' });
      if (pageParam) qs.set('cursor', pageParam);
      return apiFetch<ApiProjectDocumentsResponse>(
        `/projects/${encodeURIComponent(projectId ?? '')}/documents?${qs.toString()}`,
      );
    },
    getNextPageParam: (last) => last.next_cursor,
  });
}

export function useProjectAccounts(projectId: string | null, workspaceId: string | undefined) {
  return useQuery({
    queryKey: ['project-accounts', projectId, workspaceId],
    enabled: Boolean(projectId) && Boolean(workspaceId),
    queryFn: async () => {
      const qs = new URLSearchParams({
        workspace_id: workspaceId ?? '',
        limit: '200',
      }).toString();
      return apiFetch<ApiProjectAccountsResponse>(
        `/projects/${encodeURIComponent(projectId ?? '')}/accounts?${qs}`,
      );
    },
  });
}
